/**
 * Main App Component
 * Full authentication routing implementation with protected routes
 * Enhanced with initialization system and comprehensive error handling
 */
import React, { Suspense, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';

import { AuthLayout, AppLayout } from './components/layout';
import {
  ProtectedRoute,
  AuthErrorBoundary,
  RouteErrorBoundary,
  AuthErrorHandler,
} from './components/auth';
import {
  LoginPage,
  RegisterPage,
  ProfileSetupPage,
  AccountLinkingPage,
  PasswordResetPage,
} from './pages/auth';
import { DashboardPage, DataEntryPage } from './pages';
import { LoadingSpinner, ScrollToTop } from './components/ui';
import { useRouteAnalytics } from '@hooks/useRouteAnalytics';
import { useAppInitialization, appInitializer } from './utils/appInit';
import { env } from './utils/env';
import { APP_INFO, ROUTES, UI_CONFIG } from './constants';
import { useTaskProcessingStore } from '@stores/taskProcessing.store';
import { AuthProvider } from './components/auth';
import { featureFlags } from './config/featureFlags';

// Lazy load heavy components for better performance
const UploadPage = React.lazy(() =>
  import('./pages/UploadPage').then((module) => ({
    default: module.UploadPage,
  })),
);
const ManualEntryPage = React.lazy(() => import('./pages/ManualEntryPage'));
const InvoiceResultsPage = React.lazy(
  () => import('./pages/InvoiceResultsPage'),
);
const SalesResultsPage = React.lazy(() =>
  import('./pages/SalesResultsPage').then((module) => ({
    default: module.SalesResultsPage,
  })),
);
const InvoicesListPage = React.lazy(() => import('./pages/InvoicesListPage'));
const ProfilePage = React.lazy(() =>
  import('./pages/ProfilePage').then((module) => ({
    default: module.ProfilePage,
  })),
);
const GoalsPage = React.lazy(() => import('./pages/GoalsPage'));
const SalesDataPage = React.lazy(() => import('./pages/SalesDataPage'));
const AnalyticsPage = React.lazy(() =>
  import('./pages/AnalyticsPage').then((module) => ({
    default: module.AnalyticsPage,
  })),
);
const SuppliersPage = React.lazy(() =>
  import('./pages/SuppliersPage').then((module) => ({
    default: module.SuppliersPage,
  })),
);
const TransparencyPage = React.lazy(() =>
  import('./pages/TransparencyPage').then((module) => ({
    default: module.TransparencyPage,
  })),
);
const PublicQrSharePage = React.lazy(() =>
  import('./pages/PublicQrSharePage').then((module) => ({
    default: module.PublicQrSharePage,
  })),
);
const CompliancePage = React.lazy(() =>
  import('./pages/CompliancePage').then((module) => ({
    default: module.CompliancePage,
  })),
);
const ComplianceDetailPage = React.lazy(() =>
  import('./pages/ComplianceDetailPage').then((module) => ({
    default: module.ComplianceDetailPage,
  })),
);
const ComplianceFormPage = React.lazy(() =>
  import('./pages/ComplianceFormPage').then((module) => ({
    default: module.ComplianceFormPage,
  })),
);
const ComplianceRenewPage = React.lazy(() =>
  import('./pages/ComplianceRenewPage').then((module) => ({
    default: module.ComplianceRenewPage,
  })),
);
const UserFilesPage = React.lazy(() =>
  import('./pages/UserFilesPage').then((module) => ({
    default: module.UserFilesPage,
  })),
);

// ============================================================================
// Query Client Configuration
// ============================================================================

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        // Don't retry on 4xx errors (client errors)
        const axiosError = error as { response?: { status?: number } };
        if (
          axiosError?.response?.status &&
          axiosError.response.status >= 400 &&
          axiosError.response.status < 500
        ) {
          return false;
        }
        return failureCount < 1; // Only retry once for network/server errors
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
    mutations: {
      retry: (failureCount, error: unknown) => {
        // Don't retry mutations on client errors
        const axiosError = error as { response?: { status?: number } };
        if (
          axiosError?.response?.status &&
          axiosError.response.status >= 400 &&
          axiosError.response.status < 500
        ) {
          return false;
        }
        return failureCount < 1;
      },
    },
  },
});

// Add global error handling for React Query
queryClient.setMutationDefaults(['global-error-handler'], {
  onError: (error: unknown) => {
    // eslint-disable-next-line no-console
    console.error('Global mutation error:', error);
    // Error will be handled by global error boundary
  },
});

// ============================================================================
// Route Loading Component
// ============================================================================

const RouteLoadingFallback: React.FC = () => (
  <LoadingSpinner message="Cargando página..." />
);

// ============================================================================
// Main App Component
// ============================================================================

// Internal component to use router hooks
const AppRoutes: React.FC = () => {
  // Initialize route analytics
  useRouteAnalytics();

  // Handle app initialization
  const { isLoading, error } = useAppInitialization();

  // Subscribe only to task count to avoid array recreation loop
  const pendingTaskCount = useTaskProcessingStore(
    (state) => state.order.length,
  );
  const pendingCountRef = React.useRef<number>(pendingTaskCount);

  useEffect(() => {
    const previousCount = pendingCountRef.current;
    if (previousCount > 0 && pendingTaskCount === 0) {
      // Access store action directly without selector to avoid unstable reference
      useTaskProcessingStore.getState().clearAll();
    }
    pendingCountRef.current = pendingTaskCount;
  }, [pendingTaskCount]);

  // Show loading screen during initialization
  if (isLoading) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface)',
          zIndex: 9999,
        }}
      >
        <LoadingSpinner message={`Iniciando ${APP_INFO.NAME}...`} />
        <div style={{ marginTop: '1rem', textAlign: 'center', opacity: 0.7 }}>
          <div>Versión {APP_INFO.VERSION}</div>
          {env.isDevelopment && (
            <div style={{ fontSize: '0.8rem', marginTop: '0.5rem' }}>
              Modo desarrollo
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show error screen if initialization failed
  if (error) {
    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--surface)',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        <h1 style={{ color: 'var(--danger-600)', marginBottom: '1rem' }}>
          No se pudo abrir Foodprint
        </h1>
        <p
          style={{
            color: 'var(--text-muted)',
            marginBottom: '2rem',
            maxWidth: '500px',
          }}
        >
          No pudimos cargar tus datos. Recarga la página o contacta al soporte
          técnico.
        </p>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'var(--action-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Recargar página
          </button>
          <button
            onClick={() => appInitializer.initialize()}
            style={{
              padding: '0.75rem 1.5rem',
              background: 'transparent',
              color: 'var(--brand-600)',
              border: '2px solid var(--brand-600)',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            Reintentar
          </button>
        </div>
        {env.isDevelopment && (
          <details
            style={{ marginTop: '2rem', textAlign: 'left', maxWidth: '600px' }}
          >
            <summary style={{ cursor: 'pointer', color: 'var(--text-muted)' }}>
              Detalles del error (solo en desarrollo)
            </summary>
            <pre
              style={{
                background: 'var(--surface-strong)',
                padding: '1rem',
                borderRadius: '4px',
                fontSize: '0.8rem',
                marginTop: '1rem',
                overflow: 'auto',
              }}
            >
              {error.message}
              {error.stack && `\n\n${error.stack}`}
            </pre>
          </details>
        )}
      </div>
    );
  }

  return (
    <>
      <ScrollToTop />
      <AuthErrorHandler />
      <Routes>
        {/* Authentication routes with error boundary */}
        <Route
          path="/auth"
          element={
            <AuthErrorBoundary>
              <AuthLayout />
            </AuthErrorBoundary>
          }
        >
          <Route path="login" element={<LoginPage />} />
          <Route path="register" element={<RegisterPage />} />
          <Route path="profile-setup" element={<ProfileSetupPage />} />
          <Route path="link-account" element={<AccountLinkingPage />} />
          <Route path="password/reset" element={<PasswordResetPage />} />
        </Route>

        {/* Fully public: no session, no AppLayout, must stay outside ProtectedRoute */}
        <Route
          path="/share/q/:token"
          element={
            <Suspense fallback={<RouteLoadingFallback />}>
              <RouteErrorBoundary>
                <PublicQrSharePage />
              </RouteErrorBoundary>
            </Suspense>
          }
        />

        {/* Legacy auth route redirects */}
        <Route path="/login" element={<Navigate to="/auth/login" replace />} />
        <Route
          path="/register"
          element={<Navigate to="/auth/register" replace />}
        />
        <Route
          path="/profile-setup"
          element={<Navigate to="/auth/profile-setup" replace />}
        />
        <Route
          path="/password/reset"
          element={<Navigate to="/auth/password/reset" replace />}
        />

        {/* Protected application routes */}
        <Route
          path="/"
          element={
            <RouteErrorBoundary>
              <ProtectedRoute requireProfile={true}>
                <AppLayout />
              </ProtectedRoute>
            </RouteErrorBoundary>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="data-entry" element={<DataEntryPage />} />
          <Route
            path="transactions"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <InvoicesListPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="user-files"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <UserFilesPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="compliance"
            element={
              featureFlags.COMPLIANCE ? (
                <Suspense fallback={<RouteLoadingFallback />}>
                  <RouteErrorBoundary>
                    <CompliancePage />
                  </RouteErrorBoundary>
                </Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="compliance/:documentId"
            element={
              featureFlags.COMPLIANCE ? (
                <Suspense fallback={<RouteLoadingFallback />}>
                  <RouteErrorBoundary>
                    <ComplianceDetailPage />
                  </RouteErrorBoundary>
                </Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="compliance/new"
            element={
              featureFlags.COMPLIANCE ? (
                <Suspense fallback={<RouteLoadingFallback />}>
                  <RouteErrorBoundary>
                    <ComplianceFormPage />
                  </RouteErrorBoundary>
                </Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="compliance/:documentId/edit"
            element={
              featureFlags.COMPLIANCE ? (
                <Suspense fallback={<RouteLoadingFallback />}>
                  <RouteErrorBoundary>
                    <ComplianceFormPage />
                  </RouteErrorBoundary>
                </Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="compliance/:documentId/renew"
            element={
              featureFlags.COMPLIANCE ? (
                <Suspense fallback={<RouteLoadingFallback />}>
                  <RouteErrorBoundary>
                    <ComplianceRenewPage />
                  </RouteErrorBoundary>
                </Suspense>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
          <Route
            path="smart-sorting"
            element={
              <Navigate
                to={`${ROUTES.USER_FILES}?view=classification`}
                replace
              />
            }
          />
          <Route
            path="upload"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <UploadPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="manual"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <ManualEntryPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="invoice-results"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <InvoiceResultsPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="sales-results/:id"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <SalesResultsPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="transaction-details"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <InvoiceResultsPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="invoices"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <InvoicesListPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="profile"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <ProfilePage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="goals"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <GoalsPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="sales-data"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <SalesDataPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="analytics"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <AnalyticsPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="suppliers"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <SuppliersPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
          <Route
            path="transparency"
            element={
              <Suspense fallback={<RouteLoadingFallback />}>
                <RouteErrorBoundary>
                  <TransparencyPage />
                </RouteErrorBoundary>
              </Suspense>
            }
          />
        </Route>

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>

      {/* Global toast notifications with enhanced configuration */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: UI_CONFIG.TOAST_DURATION,
          style: {
            background: 'var(--surface)',
            color: 'var(--text-primary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-medium)',
            fontSize: '0.9rem',
            maxWidth: '400px',
          },
          success: {
            iconTheme: {
              primary: 'var(--success-600)',
              secondary: '#fff',
            },
            style: {
              borderColor: 'var(--success-600)',
            },
          },
          error: {
            iconTheme: {
              primary: 'var(--danger-600)',
              secondary: '#fff',
            },
            style: {
              borderColor: 'var(--danger-600)',
            },
          },
        }}
      />
    </>
  );
};

/**
 * Root App Component
 *
 * Features:
 * - Comprehensive initialization system with validation
 * - Advanced error handling and reporting
 * - React Query for server state management
 * - React Router with future flags enabled
 * - Global toast notifications
 * - Environment-aware configuration
 * - Development tools and debugging support
 */
function App() {
  React.useEffect(() => {
    // Mark app start for performance monitoring
    if ('performance' in window && 'mark' in performance) {
      performance.mark('app-start');
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router
          future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
        >
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
